import React, { useState, useEffect } from 'react';
import { useWindows } from '../contexts/WindowContext';
import Minesweeper from './Minesweeper';
import Tetris from './Tetris';
import Browser from './Browser';
import LiveStream from './LiveStream';
import Events from './Events';
import Chat from './Chat';
import NESEmulator from './NESEmulator';
import DOSEmulator from './DOSEmulator';
import PICO8Emulator from './PICO8Emulator';
import CollaborativeNotepad from './CollaborativeNotepad';
// import Visual from './Visual';

// Import minesweeper icon
import minesweeperIcon from '../assets/icons/minesweeper.webp';

interface LiveStreamData {
  title: string;
  stream: MediaStream;
  source: 'camera' | 'screen';
}

interface DesktopIconProps {
  id: string;
  name: string;
  icon: React.ComponentType<{ size?: number }> | string;
  x: number;
  y: number;
  type: 'file' | 'folder' | 'game';
  onPositionChange: (iconId: string, newX: number, newY: number) => void;
  isStreamLive?: boolean;
  liveStreamData?: LiveStreamData | null;
}

const DesktopIcon: React.FC<DesktopIconProps> = ({ id, name, icon, x, y, onPositionChange, isStreamLive = false, liveStreamData }) => {
  const { openWindow } = useWindows();
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, iconX: 0, iconY: 0 });

  // Detect if user is on mobile device
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                   window.innerWidth <= 768;

  // Handle mouse/touch dragging with global events
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const dx = e.clientX - dragStart.x;
        const dy = e.clientY - dragStart.y;
        const newX = Math.max(0, Math.min(window.innerWidth - 70, dragStart.iconX + dx));
        const newY = Math.max(0, Math.min(window.innerHeight - 100, dragStart.iconY + dy));
        onPositionChange(id, newX, newY);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (isDragging && e.touches.length > 0) {
        const touch = e.touches[0];
        const dx = touch.clientX - dragStart.x;
        const dy = touch.clientY - dragStart.y;
        const newX = Math.max(0, Math.min(window.innerWidth - 70, dragStart.iconX + dx));
        const newY = Math.max(0, Math.min(window.innerHeight - 100, dragStart.iconY + dy));
        onPositionChange(id, newX, newY);
      }
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging, dragStart, id, onPositionChange]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY, iconX: x, iconY: y });
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({ x: touch.clientX, y: touch.clientY, iconX: x, iconY: y });
  };

  const handleDoubleClick = () => {
    let content = <div>Default content</div>;
    
    if (id === 'my-computer') {
      content = (
        <div className="file-explorer">
          <div className="explorer-toolbar">
            <button className="explorer-button">⬅️ Back</button>
            <button className="explorer-button">🔍 Search</button>
            <button className="explorer-button">📁 Folders</button>
            <button className="explorer-button">👁️ Views</button>
          </div>
          <div className="explorer-header">
            <div className="address-bar">
              💻 Address: My Computer
            </div>
          </div>
          <div className="explorer-main">
            <div className="explorer-sidebar">
              <div className="task-section">
                <div className="task-section-title">System Tasks ⏷</div>
                <div className="task-item">
                  <span className="task-icon">👁️</span>
                  View system information
                </div>
                <div className="task-item">
                  <span className="task-icon">➕</span>
                  Add or remove programs
                </div>
                <div className="task-item">
                  <span className="task-icon">🔍</span>
                  Search for files or folders
                </div>
              </div>
              <div className="task-section">
                <div className="task-section-title">Other Places ⏷</div>
                <div className="task-item">
                  <span className="task-icon">💻</span>
                  My Network Places
                </div>
                <div className="task-item">
                  <span className="task-icon">📁</span>
                  My Documents
                </div>
                <div className="task-item">
                  <span className="task-icon">📁</span>
                  Shared Documents
                </div>
              </div>
            </div>
            <div className="explorer-content">
              <div className="drive-item">
                <div className="drive-icon">💿</div>
                <div className="drive-info">
                  <div className="drive-name">Local Disk (C:)</div>
                  <div className="drive-size">50.0 GB free of 100 GB</div>
                </div>
              </div>
              <div className="drive-item">
                <div className="drive-icon">💿</div>
                <div className="drive-info">
                  <div className="drive-name">DVD Drive (D:)</div>
                  <div className="drive-size">No disc</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (id === 'my-documents') {
      content = (
        <div className="file-explorer">
          <div className="explorer-toolbar">
            <button className="explorer-button">⬅️ Back</button>
            <button className="explorer-button">🔍 Search</button>
            <button className="explorer-button">📁 Folders</button>
            <button className="explorer-button">👁️ Views</button>
          </div>
          <div className="explorer-header">
            <div className="address-bar">
              📁 Address: My Documents
            </div>
          </div>
          <div className="explorer-main">
            <div className="explorer-sidebar">
              <div className="task-section">
                <div className="task-section-title">File and Folder Tasks ⏷</div>
                <div className="task-item">
                  <span className="task-icon">📝</span>
                  Make a new folder
                </div>
                <div className="task-item">
                  <span className="task-icon">🌐</span>
                  Publish this folder to the Web
                </div>
                <div className="task-item">
                  <span className="task-icon">🤝</span>
                  Share this folder
                </div>
              </div>
              <div className="task-section">
                <div className="task-section-title">Other Places ⏷</div>
                <div className="task-item">
                  <span className="task-icon">💻</span>
                  My Computer
                </div>
                <div className="task-item">
                  <span className="task-icon">📁</span>
                  Shared Documents
                </div>
              </div>
            </div>
            <div className="explorer-content">
              <div className="folder-item">
                <div className="folder-icon">📁</div>
                <div className="folder-info">
                  <div className="folder-name">My Pictures</div>
                </div>
              </div>
              <div className="folder-item">
                <div className="folder-icon">📁</div>
                <div className="folder-info">
                  <div className="folder-name">My Music</div>
                </div>
              </div>
              <div className="folder-item">
                <div className="folder-icon">📁</div>
                <div className="folder-info">
                  <div className="folder-name">My Videos</div>
                </div>
              </div>
              <div className="file-item">
                <div className="file-icon">📄</div>
                <div className="file-info">
                  <div className="file-name">Document.txt</div>
                </div>
              </div>
              <div className="file-item">
                <div className="file-icon">📊</div>
                <div className="file-info">
                  <div className="file-name">Presentation.ppt</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (id === 'text-file') {
      content = <CollaborativeNotepad />;
    } else if (id === 'minesweeper') {
      content = <Minesweeper />;
    } else if (id === 'live') {
      content = <LiveStream isLive={isStreamLive} streamData={liveStreamData} />;
    } else if (id === 'games') {
      content = (
        <div className="file-explorer">
          <div className="explorer-toolbar">
            <button className="explorer-button">⬅️ Back</button>
            <button className="explorer-button">🔍 Search</button>
            <button className="explorer-button">📁 Folders</button>
            <button className="explorer-button">👁️ Views</button>
          </div>
          <div className="explorer-header">
            <div className="address-bar">
              🎮 Address: Games
            </div>
          </div>
          <div className="explorer-main">
            <div className="explorer-sidebar">
              <div className="task-section">
                <div className="task-section-title">File and Folder Tasks ⏷</div>
                <div className="task-item">
                  <span className="task-icon">🎮</span>
                  Install new games
                </div>
                <div className="task-item">
                  <span className="task-icon">⚙️</span>
                  Game settings
                </div>
              </div>
              <div className="task-section">
                <div className="task-section-title">Other Places ⏷</div>
                <div className="task-item">
                  <span className="task-icon">💻</span>
                  My Computer
                </div>
                <div className="task-item">
                  <span className="task-icon">📁</span>
                  Program Files
                </div>
              </div>
            </div>
            <div className="explorer-content">
              <div 
                className="file-item"
                onClick={() => {
                  // Open Minesweeper from within Games folder
                  const minesweeperWindow = {
                    id: 'window-minesweeper-from-games',
                    title: 'Minesweeper',
                    content: <Minesweeper />,
                    x: Math.random() * 200 + 150,
                    y: Math.random() * 100 + 150,
                    width: 280,
                    height: 350,
                  };
                  openWindow(minesweeperWindow);
                }}
                style={{ cursor: 'pointer' }}
              >
                <div className="file-icon">
                  <img src={minesweeperIcon} alt="Minesweeper" width={32} height={32} />
                </div>
                <div className="file-info">
                  <div className="file-name">Minesweeper</div>
                </div>
              </div>
              <div className="file-item">
                <div className="file-icon">🎯</div>
                <div className="file-info">
                  <div className="file-name">Solitaire</div>
                </div>
              </div>
              <div 
                className="file-item"
                onClick={() => {
                  // Open Tetris from within Games folder
                  const tetrisWindow = {
                    id: 'window-tetris-from-games',
                    title: 'Tetris',
                    content: <Tetris />,
                    x: Math.random() * 200 + 150,
                    y: Math.random() * 100 + 150,
                    width: 400,
                    height: 500,
                  };
                  openWindow(tetrisWindow);
                }}
                style={{ cursor: 'pointer' }}
              >
                <div className="file-icon">🟩</div>
                <div className="file-info">
                  <div className="file-name">Tetris</div>
                </div>
              </div>
              <div className="file-item">
                <div className="file-icon">🃏</div>
                <div className="file-info">
                  <div className="file-name">Hearts</div>
                </div>
              </div>
              <div className="file-item">
                <div className="file-icon">🎲</div>
                <div className="file-info">
                  <div className="file-name">Pinball</div>
                </div>
              </div>
              <div 
                className="file-item"
                onClick={() => {
                  // Open NES Emulator
                  const nesWindow = {
                    id: 'window-nes-emulator',
                    title: 'NES Emulator',
                    content: <NESEmulator />,
                    x: Math.random() * 200 + 150,
                    y: Math.random() * 100 + 150,
                    width: 600,
                    height: 550,
                  };
                  openWindow(nesWindow);
                }}
                style={{ cursor: 'pointer' }}
              >
                <div className="file-icon">🎮</div>
                <div className="file-info">
                  <div className="file-name">NES Emulator</div>
                </div>
              </div>
              <div 
                className="file-item"
                onClick={() => {
                  // Open DOS Emulator
                  const dosWindow = {
                    id: 'window-dos-emulator',
                    title: 'DOS Emulator',
                    content: <DOSEmulator />,
                    x: Math.random() * 200 + 150,
                    y: Math.random() * 100 + 150,
                    width: 700,
                    height: 520,
                  };
                  openWindow(dosWindow);
                }}
                style={{ cursor: 'pointer' }}
              >
                <div className="file-icon">💻</div>
                <div className="file-info">
                  <div className="file-name">DOS Emulator</div>
                </div>
              </div>
              <div 
                className="file-item"
                onClick={() => {
                  // Open PICO-8 Emulator
                  const pico8Window = {
                    id: 'window-pico8-emulator',
                    title: 'Fantasy Console',
                    content: <PICO8Emulator />,
                    x: Math.random() * 200 + 150,
                    y: Math.random() * 100 + 150,
                    width: 520,
                    height: 400,
                  };
                  openWindow(pico8Window);
                }}
                style={{ cursor: 'pointer' }}
              >
                <div className="file-icon">🕹️</div>
                <div className="file-info">
                  <div className="file-name">Fantasy Console</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (id === 'browser') {
      content = <Browser />;
    } else if (id === 'events') {
      content = <Events />;
    } else if (id === 'chat') {
      content = <Chat 
        streamId={1} 
      />;
    } else if (id === 'visual') {
      // content = <Visual />;
    }

    const windowConfig = {
      id: `window-${id}`,
      title: name,
      content,
      x: Math.random() * 200 + 100,
      y: Math.random() * 100 + 100,
      width: id === 'minesweeper' ? 280 : id === 'live' ? 900 : id === 'browser' ? 800 : id === 'tetris' ? 400 : id === 'events' ? 800 : id === 'chat' ? 500 : id === 'visual' ? 550 : 600,
      height: id === 'minesweeper' ? 350 : id === 'live' ? 600 : id === 'browser' ? 600 : id === 'tetris' ? 500 : id === 'events' ? 600 : id === 'chat' ? 650 : id === 'visual' ? 450 : 400,
    };

    openWindow(windowConfig);
  };

  const handleClick = () => {
    if (isMobile) {
      handleDoubleClick(); // On mobile, single click acts like double click
    }
  };

  return (
    <div
      className={`desktop-icon ${isDragging ? 'dragging' : ''}`}
      style={{ left: x, top: y }}
      onDoubleClick={!isMobile && !isDragging ? handleDoubleClick : undefined}
      onClick={isMobile && !isDragging ? handleClick : undefined}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <div className="icon-image">
        {typeof icon === 'string' ? (
          <img src={icon} alt={name} width={32} height={32} />
        ) : (
          React.createElement(icon, { size: 32 })
        )}
      </div>
      <div className="icon-label">{name}</div>
    </div>
  );
};

export default DesktopIcon;